import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { api } from '@/lib/api';
import type { Program } from '@/lib/types';
import { fmtDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/field';
import { Alert, Badge, Card, Spinner } from '@/components/ui/misc';
import { useToast } from '@/components/ui/toast';

interface Row {
  id: string;
  kind: string;
  subject: string;
  link: string;
  status: 'sent' | 'not_sent' | 'failed';
  error: string | null;
  createdAt: string;
  sentAt: string | null;
  recipient: { id: string; name: string; email: string };
}

const KIND: Record<string, string> = {
  report_published: 'Report published',
  deliverable_submitted: 'Deliverable submitted',
  revisions_requested: 'Revisions requested',
  comment_mention: 'Mentioned in a comment',
};
const STATUS = {
  sent: { label: 'Sent', tone: 'ok' },
  not_sent: { label: 'Not sent', tone: 'warn' },
  failed: { label: 'Failed', tone: 'bad' },
} as const;

export function Notifications({ program }: { program: Program }) {
  const [status, setStatus] = useState('');
  const toast = useToast();
  const q = useQuery({
    queryKey: ['admin', program.id, 'notifications', status],
    queryFn: () =>
      api.get<{ mailEnabled: boolean; notifications: Row[] }>(
        `/api/programs/${program.id}/admin/notifications${status ? `?status=${status}` : ''}`,
      ),
  });

  return (
    <div className="space-y-4">
      {q.data && !q.data.mailEnabled && (
        <Alert tone="info" title="Email is not configured">
          HELM logs each notification here instead of sending it. Copy the link and share it yourself. Links open the
          record after sign-in; they carry no report content.
        </Alert>
      )}
      <div className="flex items-center gap-2">
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-44" aria-label="Status">
          <option value="">All statuses</option>
          <option value="not_sent">Not sent</option>
          <option value="failed">Failed</option>
          <option value="sent">Sent</option>
        </Select>
      </div>
      <Card className="overflow-hidden p-0">
        {q.isLoading ? (
          <div className="flex justify-center p-8">
            <Spinner />
          </div>
        ) : !q.data?.notifications.length ? (
          <p className="p-6 text-sm text-muted">No notifications yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">To</th>
                <th className="px-4 py-2 font-medium">Event</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {q.data.notifications.map((n) => (
                <tr key={n.id} className="border-b border-border last:border-0 align-top">
                  <td className="whitespace-nowrap px-4 py-2.5 text-muted">{fmtDateTime(n.createdAt)}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{n.recipient.name}</div>
                    <div className="text-xs text-muted">{n.recipient.email}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div>{KIND[n.kind] ?? n.kind}</div>
                    <div className="text-xs text-muted">{n.subject}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge tone={STATUS[n.status].tone}>{STATUS[n.status].label}</Badge>
                    {n.error && <div className="mt-1 text-xs text-muted">{n.error}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        void navigator.clipboard.writeText(n.link);
                        toast.ok('Link copied.');
                      }}
                    >
                      <Copy /> Copy link
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
